import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, CalendarDays, Palette, Briefcase, Building2, Users, ArrowRight,
  Sparkles, Quote, Film, Brush, PaintBucket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Section, SectionHeading, Eyebrow, ArrowLink } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Collaborators } from "@/components/Collaborators";
import { useDialogs } from "@/components/GlobalDialogs";
import { getCourses, getEarn, getEvents } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1513364776144-60967b0f800f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CHOOSER = [
  { icon: BookOpen, verb: "Learn", text: "Courses & free workbooks", to: "/learn-earn", testid: "home-chooser-learn-card" },
  { icon: CalendarDays, verb: "Classes", text: "Art classes & study sessions", to: "/events", testid: "home-chooser-attend-card" },
  { icon: Palette, verb: "Create", text: "Build a real practice", to: "/learn-earn", testid: "home-chooser-create-card" },
  { icon: Briefcase, verb: "Earn", text: "Turn skills into work", to: "/learn-earn", testid: "home-chooser-earn-card" },
  { icon: Building2, verb: "Use the Studio", text: "A space to study & practise", to: "/studio", testid: "home-chooser-studio-card" },
  { icon: Users, verb: "Collaborate", text: "Build with us", to: "/contact", testid: "home-chooser-collaborate-card" },
];

const PRINCIPLES = [
  { n: "01", title: "Creativity is inherent", text: "Everyone is born creative. The goal isn't to gift talent — it's to remove what got in the way." },
  { n: "02", title: "Art belongs in everyday life", text: "Not confined to galleries or careers — a practice woven into how we live, gather and see." },
  { n: "03", title: "We make together", text: "A living community over an institution. We learn faster, braver and kinder in each other's company." },
  { n: "04", title: "A future for artists", text: "We're working toward a world where art is an appreciated and respectable profession." },
];

// PAST WORK — now told as stories on the Blog (links to /blog/*)
const CREATED = [
  { title: "Colours of Delhi", tag: "Community Art", icon: PaintBucket, to: "/blog/colours-of-delhi-story", span: "lg:col-span-2" },
  { title: "Short Film Screenings", tag: "Film", icon: Film, to: "/blog/short-film-screenings" },
  { title: "Musical Baithak", tag: "Music", icon: Palette, to: "/blog/musical-baithak" },
  { title: "Wellness Conversations", tag: "Community", icon: Users, to: "/blog/wellness-conversations" },
  { title: "Live Study Workshop", tag: "Live Study", icon: Brush, to: "/blog/live-study-workshop", span: "lg:col-span-2" },
];

const TESTIMONIALS = [
  { quote: "For the first time in years, I'm making again — and it doesn't feel precious or scary. It feels like play.", name: "A YTCA participant" },
  { quote: "Studying alongside other artists changed how I practise. You come for the art and stay for the people.", name: "A studio regular" },
  { quote: "I always believed I 'couldn't draw'. Turns out I just never learned how to see.", name: "A first-time learner" },
];

export default function Home() {
  const { openJoin, openWaitlist, openCollaborate } = useDialogs();
  const [courses, setCourses] = useState([]);
  const [earn, setEarn] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    getCourses().then(setCourses).catch(() => {});
    getEarn().then((d) => setEarn(d.categories || [])).catch(() => {});
    getEvents().then(setEvents).catch(() => {});
  }, []);

  const featured = courses.slice(0, 3);
  const upcoming = events.slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden noise">
        <div className="pointer-events-none absolute inset-0" style={{
          background:
            "radial-gradient(900px circle at 12% 8%, rgba(23,182,166,0.16), transparent 55%), radial-gradient(700px circle at 88% 18%, rgba(244,240,74,0.20), transparent 60%)",
        }} />
        <div className="ytca-container relative grid gap-12 py-16 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:py-28">
          <div>
            <Eyebrow>An art education movement</Eyebrow>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-display text-5xl font-semibold leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl"
            >
              You Too<br />Can Art
            </motion.h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
              YTCA is an art education platform. We help everyone learn to make — through structured
              courses, live study, workshops and a studio space for focused practice.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button onClick={openJoin} size="lg" className="rounded-full" data-testid="hero-join-button">
                Join YTCA <ArrowRight className="h-4 w-4" />
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full" data-testid="hero-learn-button">
                <Link to="/learn-earn">Start Learning</Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="rounded-full" data-testid="hero-studio-button">
                <Link to="/events">Browse Classes</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-2 text-xs text-muted-foreground">
              {["Courses", "Live Study", "Workshops", "Self-Study", "Studio"].map((t) => (
                <span key={t} className="rounded-full border border-border bg-secondary/60 px-3 py-1">{t}</span>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl border border-border shadow-[0_20px_60px_rgba(20,20,20,0.12)]">
                <img src={HERO_IMG} alt="People making art together in a bright studio" className="aspect-[4/5] w-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-border bg-card px-5 py-4 shadow-lg sm:block">
                <p className="font-display text-lg font-semibold">Made together</p>
                <p className="text-xs text-muted-foreground">Community over institution</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CHOOSER */}
      <section className="border-y border-border bg-secondary/40">
        <div className="ytca-container py-14">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">What would you like to do?</h2>
            <p className="text-sm text-muted-foreground">Pick a path — every one leads somewhere real.</p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
            {CHOOSER.map((c, i) => (
              <Reveal key={c.verb} delay={i * 0.05}>
                <Link to={c.to} data-testid={c.testid} className="group block h-full">
                  <Card className="card-lift h-full rounded-2xl border-border bg-card p-5 hover:border-primary/40">
                    <c.icon className="h-6 w-6 text-primary" />
                    <p className="mt-4 font-display text-lg font-semibold">{c.verb}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{c.text}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-foreground/70 transition-transform group-hover:translate-x-1">Go →</span>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE IDEA */}
      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <SectionHeading eyebrow="The Idea" title="Creativity isn't a gift for the few." />
          <div>
            <Quote className="h-10 w-10 text-primary/40" />
            <p className="mt-4 font-display text-2xl font-medium leading-snug sm:text-3xl">
              We were told art belongs to the talented. We think that's the biggest lie in creative
              life. The impulse to make is universal — what's rare is a world that keeps room for it.
            </p>
            <p className="mt-6 max-w-xl text-muted-foreground">
              YTCA wants to bring creativity back into everyday life — as a practice available to
              everyone, not a career reserved for a few.
            </p>
            <div className="mt-6"><ArrowLink to="/about" testid="idea-read-movement-link">Read the movement</ArrowLink></div>
          </div>
        </div>
      </Section>

      {/* THE MOVEMENT */}
      <section className="ytca-section bg-secondary/40">
        <div className="ytca-container">
          <Reveal>
            <SectionHeading eyebrow="The Movement" title="More than courses. A way of living creatively." intro="YTCA is a movement, not a workshop business. These are the beliefs we build everything around." />
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <Card className="h-full rounded-2xl border-border bg-card p-6">
                  <span className="font-display text-3xl font-semibold text-primary">{p.n}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
          <div className="mt-8"><ArrowLink onClick={openJoin} testid="movement-join-inspiration-link">Join daily inspiration</ArrowLink></div>
        </div>
      </section>

      {/* ACHIEVEMENTS (PAST WORK) */}
      <Section>
        <SectionHeading eyebrow="Achievements" title="What YTCA has achieved so far." intro="A look back at our past projects and community art initiatives — the achievements that show the movement in action, now told as stories on our blog." />
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CREATED.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.05} className={item.span || ""}>
              <Link to={item.to} data-testid={`created-card-${i}`} className="group block h-full">
                <Card className="card-lift relative flex h-full min-h-[180px] flex-col justify-between overflow-hidden rounded-2xl border-border bg-card p-6 hover:border-primary/40">
                  <div className="flex items-center justify-between">
                    <item.icon className="h-6 w-6 text-primary" />
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-full bg-[#141414] text-white">Achievement</Badge>
                      <Badge variant="secondary" className="rounded-full">{item.tag}</Badge>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{item.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground transition-transform group-hover:translate-x-1">Read the story →</span>
                  </div>
                </Card>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><ArrowLink to="/blog" testid="created-explore-work-link">Explore Our Achievements on the Blog</ArrowLink></div>
      </Section>

      {/* COLLABORATIONS */}
      <Collaborators />

      {/* CLASSES & SESSIONS (BOOKABLE) */}
      <section className="ytca-section bg-secondary/40">
        <div className="ytca-container">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading eyebrow="Classes & Sessions" title="Learn with structured classes & study sessions." intro="Live study, workshops and self-study sessions — guided art education you can join and learn from." />
              <Button asChild variant="outline" className="rounded-full" data-testid="experience-see-all-button">
                <Link to="/events">See All Classes</Link>
              </Button>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {upcoming.map((e, i) => (
              <Reveal key={e.slug} delay={i * 0.05}>
                <Link to={`/events/${e.slug}`} data-testid={`upcoming-event-${e.slug}`} className="group block h-full">
                  <Card className="card-lift h-full overflow-hidden rounded-2xl border-border bg-card hover:border-primary/40">
                    <div className="relative">
                      <img src={e.image} alt={e.title} className="aspect-[16/10] w-full object-cover" />
                      <Badge className="absolute left-3 top-3 rounded-full bg-primary text-primary-foreground">{e.category_label}</Badge>
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{e.category_label}</span>
                      <h3 className="mt-1 font-display text-lg font-semibold">{e.title}</h3>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-display text-base font-semibold">{e.is_free ? "Free" : `₹${e.price}`}</span>
                        <span className="text-xs font-medium text-primary transition-transform group-hover:translate-x-1">View →</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEARN WITH YTCA */}
      <section className="ytca-section">
        <div className="ytca-container">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading eyebrow="Learn With YTCA" title="Free workbooks. Paid courses. Real progress." />
              <Button asChild variant="outline" className="rounded-full" data-testid="learn-browse-courses-button">
                <Link to="/learn-earn">Browse Courses</Link>
              </Button>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {featured.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.06}>
                <Link to={`/courses/${c.slug}`} data-testid={`home-course-${c.slug}`} className="group block h-full">
                  <Card className="card-lift h-full overflow-hidden rounded-2xl border-border bg-card hover:border-primary/40">
                    <img src={c.image} alt={c.title} className="aspect-[16/10] w-full object-cover" />
                    <div className="p-5">
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-full ${c.kind === "free" ? "bg-accent text-accent-foreground" : ""}`}>{c.kind === "free" ? "Free" : `₹${c.price}`}</Badge>
                        <span className="text-xs text-muted-foreground">{c.level}</span>
                      </div>
                      <h3 className="mt-3 font-display text-lg font-semibold">{c.title}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.summary}</p>
                    </div>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EARN WITH YTCA */}
      <Section id="earn">
        <SectionHeading eyebrow="Earn With YTCA" title="Learn a skill. Build your identity. Find opportunities to use it." intro="An opportunity ecosystem for people who already make — or want to make a living from it." />
        <div className="mt-8 flex flex-wrap gap-2">
          {earn.slice(0, 12).map((c) => (
            <span key={c.key} className="rounded-full border border-border bg-card px-4 py-2 text-sm">{c.title}</span>
          ))}
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Build a skill", d: "Through our courses, workshops and community practice." },
            { n: "2", t: "Shape your identity", d: "Develop a body of work and a creative voice that's yours." },
            { n: "3", t: "Find opportunities", d: "Commissions, collaborations and project work with YTCA." },
          ].map((s) => (
            <Card key={s.n} className="rounded-2xl border-border bg-secondary/40 p-6">
              <span className="font-display text-2xl font-semibold text-primary">{s.n}</span>
              <h3 className="mt-2 font-display text-lg font-semibold">{s.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
            </Card>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={() => openWaitlist("Earn With YTCA")} className="rounded-full" data-testid="earn-waitlist-button">Join the Waitlist</Button>
          <Button asChild variant="outline" className="rounded-full" data-testid="earn-explore-button"><Link to="/learn-earn">Explore Earn</Link></Button>
        </div>
      </Section>

      {/* STUDIO */}
      <section className="ytca-section bg-[#141414] text-white noise">
        <div className="ytca-container grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <img src="https://images.unsplash.com/photo-1558470598-a5dda9640f68?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="YTCA studio space" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Eyebrow className="text-white/70">YTCA Studio</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">A study space to practise and learn.</h2>
            <p className="mt-4 max-w-lg text-white/70">
              Book focused self-study time, join as a member, or collaborate with us to run a study
              session. The studio is where learning becomes a regular, shared practice.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full" data-testid="studio-book-button"><Link to="/studio">Book Self-Study</Link></Button>
              <Button asChild variant="outline" size="lg" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10" data-testid="studio-explore-button"><Link to="/studio">Explore the Space</Link></Button>
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY / RECOGNITION */}
      <Section>
        <SectionHeading eyebrow="Community" title="A living creative community." intro="Voices from people making with us. (Real recognitions and testimonials will appear here as the community grows.)" />
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <Card className="h-full rounded-2xl border-border bg-card p-6">
                <Sparkles className="h-5 w-5 text-primary" />
                <p className="mt-4 font-display text-lg leading-snug">“{t.quote}”</p>
                <p className="mt-4 text-sm text-muted-foreground">— {t.name}</p>
              </Card>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><ArrowLink onClick={openCollaborate} testid="community-collaborate-link">Collaborate with us</ArrowLink></div>
      </Section>

      {/* JOIN BAND */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground noise">
        <div className="ytca-container py-16 text-center sm:py-20">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-semibold sm:text-5xl">You too can art. Start today.</h2>
            <p className="mx-auto mt-4 max-w-xl text-primary-foreground/90">
              Join the movement for daily inspiration and first access to classes, courses and study sessions.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button onClick={openJoin} size="lg" variant="secondary" className="rounded-full" data-testid="join-band-button">Join YTCA</Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="join-band-events-button"><Link to="/events">Browse Classes</Link></Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
